import React, { useState, useEffect } from 'react';
import { equipo, clientesRentabilidad } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import { 
  UserCog, Plus, Edit2, Trash2, Users, DollarSign, 
  Briefcase, Clock, Mail, Phone, Calendar, Link2
} from 'lucide-react';

const Equipo = () => {
  const [colaboradores, setColaboradores] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showAsignDialog, setShowAsignDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [selectedColaborador, setSelectedColaborador] = useState(null);
  const [asignaciones, setAsignaciones] = useState([]);
  
  // Form
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    tipo: 'tiempo_completo',
    costo_mensual: '',
    costo_por_hora: '',
    especialidad: ''
  });

  const [asignForm, setAsignForm] = useState({
    cliente_rentabilidad_id: '',
    horas_mensuales: '',
    porcentaje_dedicacion: ''
  });

  const fetchData = async () => {
    setLoading(true);
    try {
      const [colabRes, clientesRes] = await Promise.all([
        equipo.getAll(),
        clientesRentabilidad.getAll()
      ]);
      setColaboradores(colabRes.data);
      setClientes(clientesRes.data);
    } catch (error) {
      toast.error('Error al cargar equipo');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const fetchAsignaciones = async (colaboradorId) => {
    try {
      const response = await equipo.getAsignaciones(colaboradorId);
      setAsignaciones(response.data.asignaciones || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleSubmit = async () => {
    if (!formData.nombre) {
      toast.error('El nombre es requerido');
      return;
    }

    try {
      if (editingItem) {
        await equipo.update(editingItem.id, formData);
        toast.success('Colaborador actualizado');
      } else {
        await equipo.create(formData);
        toast.success('Colaborador creado');
      }
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este colaborador y sus asignaciones?')) return;
    try {
      await equipo.delete(id);
      toast.success('Colaborador eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const handleAddAsignacion = async () => {
    if (!asignForm.cliente_rentabilidad_id) {
      toast.error('Selecciona un cliente');
      return;
    }
    try {
      await equipo.createAsignacion({
        colaborador_id: selectedColaborador.id,
        ...asignForm
      });
      toast.success('Asignación creada');
      fetchAsignaciones(selectedColaborador.id);
      setAsignForm({ cliente_rentabilidad_id: '', horas_mensuales: '', porcentaje_dedicacion: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al asignar');
    }
  };

  const handleDeleteAsignacion = async (asignacionId) => {
    try {
      await equipo.deleteAsignacion(asignacionId);
      toast.success('Asignación eliminada');
      fetchAsignaciones(selectedColaborador.id);
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const resetForm = () => {
    setFormData({
      nombre: '',
      email: '',
      telefono: '',
      tipo: 'tiempo_completo',
      costo_mensual: '',
      costo_por_hora: '',
      especialidad: ''
    });
    setEditingItem(null);
  };

  const openEdit = (item) => {
    setEditingItem(item);
    setFormData({
      nombre: item.nombre,
      email: item.email || '',
      telefono: item.telefono || '',
      tipo: item.tipo || 'tiempo_completo',
      costo_mensual: item.costo_mensual || '',
      costo_por_hora: item.costo_por_hora || '',
      especialidad: item.especialidad || ''
    });
    setShowDialog(true);
  };

  const openAsignaciones = async (item) => {
    setSelectedColaborador(item);
    await fetchAsignaciones(item.id);
    setShowAsignDialog(true);
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(value || 0);
  };

  const getTipoBadge = (tipo) => {
    const tipos = {
      'tiempo_completo': { label: 'Tiempo Completo', color: 'bg-blue-100 text-blue-800' },
      'medio_tiempo': { label: 'Medio Tiempo', color: 'bg-amber-100 text-amber-800' },
      'freelance': { label: 'Freelance', color: 'bg-purple-100 text-purple-800' },
      'contratista': { label: 'Contratista', color: 'bg-slate-100 text-slate-800' }
    };
    return tipos[tipo] || tipos['tiempo_completo'];
  };

  // Stats
  const stats = {
    total: colaboradores.length,
    tiempoCompleto: colaboradores.filter(c => c.tipo === 'tiempo_completo').length,
    freelance: colaboradores.filter(c => c.tipo === 'freelance' || c.tipo === 'contratista').length,
    costoTotal: colaboradores.reduce((sum, c) => sum + (c.costo_mensual || 0), 0)
  };

  return (
    <div className="space-y-6 p-4 md:p-6" data-testid="equipo-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <UserCog className="w-6 h-6 text-primary" />
            Gestión de Equipo
          </h1>
          <p className="text-muted-foreground text-sm">Administra colaboradores y sus asignaciones</p>
        </div>
        <Button onClick={() => { resetForm(); setShowDialog(true); }} data-testid="new-member-btn">
          <Plus className="w-4 h-4 mr-2" />
          Nuevo Colaborador
        </Button>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Total Equipo</p>
                <p className="text-2xl font-bold">{stats.total}</p>
              </div>
              <Users className="w-8 h-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Tiempo Completo</p>
                <p className="text-2xl font-bold">{stats.tiempoCompleto}</p>
              </div>
              <Clock className="w-8 h-8 text-emerald-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Freelance/Ext.</p>
                <p className="text-2xl font-bold">{stats.freelance}</p>
              </div>
              <Briefcase className="w-8 h-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">Costo Mensual</p>
                <p className="text-xl font-bold">{formatCurrency(stats.costoTotal)}</p>
              </div>
              <DollarSign className="w-8 h-8 text-amber-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Team List */}
      <Card>
        <CardHeader>
          <CardTitle>Colaboradores</CardTitle>
          <CardDescription>Haz clic en "Asignar" para ver y gestionar clientes asignados</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Cargando...</div>
          ) : colaboradores.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Users className="w-12 h-12 mx-auto mb-2 opacity-50" />
              <p>No hay colaboradores registrados</p>
              <Button variant="outline" className="mt-4" onClick={() => { resetForm(); setShowDialog(true); }}>
                Agregar primer colaborador
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {colaboradores.map((item) => {
                const tipoInfo = getTipoBadge(item.tipo);
                return (
                  <Card key={item.id} className="hover:shadow-md transition-shadow" data-testid={`member-card-${item.id}`}>
                    <CardContent className="pt-4">
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center">
                            <span className="text-lg font-bold text-primary">
                              {item.nombre?.charAt(0)?.toUpperCase()}
                            </span>
                          </div>
                          <div>
                            <h3 className="font-semibold">{item.nombre}</h3>
                            <Badge className={tipoInfo.color} variant="outline">{tipoInfo.label}</Badge>
                          </div>
                        </div>
                      </div>
                      
                      {item.especialidad && (
                        <p className="text-sm text-muted-foreground mb-2">{item.especialidad}</p>
                      )}
                      
                      <div className="space-y-1 text-sm mb-4">
                        {item.email && (
                          <p className="flex items-center gap-2 text-muted-foreground">
                            <Mail className="w-4 h-4" />
                            {item.email}
                          </p>
                        )}
                        {item.telefono && (
                          <p className="flex items-center gap-2 text-muted-foreground">
                            <Phone className="w-4 h-4" />
                            {item.telefono}
                          </p>
                        )}
                      </div>
                      
                      <div className="flex items-center justify-between border-t pt-3">
                        <div>
                          <p className="text-xs text-muted-foreground">Costo Mensual</p>
                          <p className="font-semibold">{formatCurrency(item.costo_mensual)}</p>
                        </div>
                        <div className="flex gap-1">
                          <Button variant="outline" size="sm" onClick={() => openAsignaciones(item)}>
                            <Link2 className="w-4 h-4 mr-1" />
                            Asignar
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => openEdit(item)}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                          <Button variant="ghost" size="icon" onClick={() => handleDelete(item.id)}>
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{editingItem ? 'Editar Colaborador' : 'Nuevo Colaborador'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 max-h-[60vh] overflow-y-auto">
            <div>
              <Label>Nombre *</Label>
              <Input
                value={formData.nombre}
                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                placeholder="Nombre completo"
                data-testid="form-name"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="correo@ejemplo.com"
                />
              </div>
              <div>
                <Label>Teléfono</Label>
                <Input
                  value={formData.telefono}
                  onChange={(e) => setFormData({ ...formData, telefono: e.target.value })}
                  placeholder="+52 55..."
                />
              </div>
            </div>
            <div>
              <Label>Tipo de Contratación</Label>
              <Select value={formData.tipo} onValueChange={(v) => setFormData({ ...formData, tipo: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="tiempo_completo">Tiempo Completo</SelectItem>
                  <SelectItem value="medio_tiempo">Medio Tiempo</SelectItem>
                  <SelectItem value="freelance">Freelance</SelectItem>
                  <SelectItem value="contratista">Contratista</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Especialidad</Label>
              <Input
                value={formData.especialidad}
                onChange={(e) => setFormData({ ...formData, especialidad: e.target.value })}
                placeholder="Ej: Diseño UX, Marketing Digital..."
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Costo Mensual</Label>
                <Input
                  type="number"
                  value={formData.costo_mensual}
                  onChange={(e) => setFormData({ ...formData, costo_mensual: parseFloat(e.target.value) || '' })}
                  placeholder="0.00"
                />
              </div>
              <div>
                <Label>Costo por Hora</Label>
                <Input
                  type="number"
                  value={formData.costo_por_hora}
                  onChange={(e) => setFormData({ ...formData, costo_por_hora: parseFloat(e.target.value) || '' })}
                  placeholder="0.00"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDialog(false)}>Cancelar</Button>
            <Button onClick={handleSubmit} data-testid="submit-member">
              {editingItem ? 'Guardar' : 'Crear'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Asignaciones Dialog */}
      <Dialog open={showAsignDialog} onOpenChange={setShowAsignDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              Asignaciones de {selectedColaborador?.nombre}
            </DialogTitle>
            <DialogDescription>
              Gestiona los clientes asignados a este colaborador
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Nueva asignación */}
            <Card>
              <CardContent className="pt-4">
                <p className="text-sm font-medium mb-3">Nueva Asignación</p>
                <div className="space-y-3">
                  <div>
                    <Label>Cliente</Label>
                    <Select value={asignForm.cliente_rentabilidad_id} onValueChange={(v) => setAsignForm({ ...asignForm, cliente_rentabilidad_id: v })}>
                      <SelectTrigger>
                        <SelectValue placeholder="Selecciona cliente" />
                      </SelectTrigger>
                      <SelectContent>
                        {clientes.map(c => <SelectItem key={c.id} value={c.id}>{c.nombre}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label>Horas/Mes</Label>
                      <Input
                        type="number"
                        value={asignForm.horas_mensuales}
                        onChange={(e) => setAsignForm({ ...asignForm, horas_mensuales: parseFloat(e.target.value) || '' })}
                        placeholder="160"
                      />
                    </div>
                    <div>
                      <Label>% Dedicación</Label>
                      <Input
                        type="number"
                        value={asignForm.porcentaje_dedicacion}
                        onChange={(e) => setAsignForm({ ...asignForm, porcentaje_dedicacion: parseFloat(e.target.value) || '' })}
                        placeholder="100"
                      />
                    </div>
                  </div>
                  <Button className="w-full" onClick={handleAddAsignacion}>
                    <Plus className="w-4 h-4 mr-2" />
                    Asignar a Cliente
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            {/* Lista de asignaciones */}
            <div>
              <p className="text-sm font-medium mb-2">Clientes Asignados ({asignaciones.length})</p>
              {asignaciones.length === 0 ? (
                <p className="text-sm text-muted-foreground text-center py-4">
                  No hay asignaciones
                </p>
              ) : (
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {asignaciones.map((a) => (
                    <div key={a.id} className="flex justify-between items-center p-3 border rounded-lg">
                      <div>
                        <p className="font-medium">{a.cliente_nombre || 'Cliente'}</p>
                        <p className="text-xs text-muted-foreground">
                          {a.horas_mensuales && `${a.horas_mensuales} hrs/mes`}
                          {a.porcentaje_dedicacion && ` · ${a.porcentaje_dedicacion}%`}
                        </p>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="icon" 
                        onClick={() => handleDeleteAsignacion(a.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Equipo;
